import { useState } from 'react';
import axios from 'axios';
import { Save } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { RichTextEditor } from '../RichTextEditor';
import { ImageUploader } from '../ImageUploader';
import { API, getAuthHeaders } from './authHelpers';
import { logError } from '../../lib/logger';

export function PostEditor({ post, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    featured_image: post?.featured_image || '',
    category: post?.category || '',
    tags: post?.tags?.join(', ') || '',
    author: post?.author || 'ServeNetics Team',
    status: post?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
    };

    try {
      const headers = getAuthHeaders();
      if (post?.slug) {
        await axios.put(`${API}/blog/${post.slug}`, payload, { headers });
      } else {
        await axios.post(`${API}/blog`, payload, { headers });
      }
      onSave();
    } catch (error) {
      logError('Error saving post:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        window.location.reload();
      } else {
        alert('Error saving post. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold text-white">
            {post ? 'Edit Post' : 'Create New Post'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="post-editor-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="title" className="text-white/80">Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter post title"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="post-title-input"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="excerpt" className="text-white/80">Excerpt *</Label>
              <Textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                required
                placeholder="Brief summary of the post (shown in previews)"
                rows={2}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                data-testid="post-excerpt-input"
              />
            </div>

            <div className="col-span-2">
              <Label className="text-white/80 mb-2 block">Content *</Label>
              <RichTextEditor
                content={formData.content}
                onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                placeholder="Write your blog post content here..."
                data-testid="post-content-input"
              />
            </div>

            <div className="col-span-2">
              <ImageUploader
                value={formData.featured_image}
                onChange={(url) => setFormData(prev => ({ ...prev, featured_image: url }))}
                label="Featured Image"
              />
            </div>

            <div>
              <Label htmlFor="category" className="text-white/80">Category</Label>
              <Input
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="e.g., AI Insights, Impact Stories"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="post-category-input"
              />
            </div>

            <div>
              <Label htmlFor="tags" className="text-white/80">Tags (comma separated)</Label>
              <Input
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="AI, Business, Technology"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="post-tags-input"
              />
            </div>

            <div>
              <Label htmlFor="author" className="text-white/80">Author</Label>
              <Input
                id="author"
                name="author"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="post-author-input"
              />
            </div>

            <div className="col-span-2">
              <Label className="text-white/80 mb-2 block">Status</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={formData.status === 'draft'}
                    onChange={handleChange}
                    className="w-4 h-4 accent-neon-purple"
                  />
                  <span className="text-white/70">Draft</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="published"
                    checked={formData.status === 'published'}
                    onChange={handleChange}
                    className="w-4 h-4 accent-neon-purple"
                  />
                  <span className="text-white/70">Published</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-neon-purple hover:bg-neon-purple/90 text-white"
              data-testid="save-post-btn"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {post ? 'Update Post' : 'Create Post'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PostEditor;
